import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Sparkles, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAccess, DEFAULT_PLAN } from "@/context/AccessContext";
import { PlanPicker } from "./PlanPicker";
import { WinBackBanner } from "./WinBackBanner";
import { PaywallActions } from "./PaywallActions";
import { toast } from "sonner";

const PERKS = [
  "All 7 statistical strategies — Monte Carlo, Markov, Hot, Overdue & more",
  "AI Oracle picks from GPT-5.2 and Claude, plus the Council consensus",
  "Full analytics: frequency, hot/cold, overdue, pair heatmaps",
  "Complete draw history back to 2010 + Oracle Scoreboard backtests",
  "Save picks, week picks, printable slips, share cards & result alerts",
];
const FALLBACK_PLAN = { price_usd: 4.99, interval: "month", label: "Monthly" };

export function Paywall() {
  const { user } = useAuth();
  const { plans, checkingOut, subscribe, buyPassWithPaypal, trial_days, paypal } = useAccess();
  const [plan, setPlan] = useState(DEFAULT_PLAN);
  const [via, setVia] = useState("");
  const selected = plans.find((p) => p.lookup_key === plan) || FALLBACK_PLAN;
  const per = selected.interval === "year" ? "year" : "month";

  const buy = async (provider = "stripe") => {
    setVia(provider);
    try {
      await subscribe(plan, provider);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    }
  };
  const buyPass = async () => {
    setVia("pass");
    try {
      await buyPassWithPaypal();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start PayPal checkout");
    }
  };

  return (
    <section id="oracle" data-testid="paywall-section" className="relative py-16 sm:py-24 px-4 sm:px-8 lg:px-12">
      <div className="relative max-w-4xl mx-auto text-center">
        <div className="label-caps text-oracle">The Oracle Awaits</div>
        <h1 className="font-heading font-light tracking-tighter text-parchment text-5xl sm:text-6xl lg:text-7xl mt-4">
          Ask the numbers.
          <br />
          <span className="italic text-oracle">Unlock their answer.</span>
        </h1>
        <p className="font-body text-parchment/60 text-sm sm:text-base mt-6 max-w-2xl mx-auto leading-relaxed">
          The latest winning numbers are free for everyone. AI predictions, analytics and full draw history are for subscribers —
          start with a {trial_days}-day free trial and cancel anytime.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          data-testid="paywall-card"
          className="mt-12 mx-auto max-w-2xl text-left bg-black/40 backdrop-blur-xl border border-oracle/30 rounded-xl p-6 sm:p-8 shadow-[0_0_60px_rgba(255,176,0,0.12)]"
        >
          <div className="flex items-center justify-between">
            <div className="label-caps text-oracle flex items-center gap-2">
              <Lock className="w-3 h-3" strokeWidth={1.5} /> Oracle Subscription
            </div>
            <span data-testid="paywall-trial-badge" className="label-caps text-[10px] px-2.5 py-1 rounded-full bg-oracle/15 border border-oracle/40 text-oracle flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={1.5} /> {trial_days}-day free trial
            </span>
          </div>

          <WinBackBanner />

          <PlanPicker plans={plans} value={plan} onChange={setPlan} />

          <div className="mt-6 flex items-end gap-2">
            <span data-testid="paywall-price" className="font-heading text-6xl leading-none text-parchment">${selected.price_usd.toFixed(2)}</span>
            <span className="font-body text-parchment/50 text-sm mb-2">/ {per} after your free trial</span>
          </div>
          <ul className="mt-6 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 font-body text-sm text-parchment/80">
                <Check className="w-4 h-4 mt-0.5 text-oracle shrink-0" strokeWidth={2} /> {p}
              </li>
            ))}
          </ul>
          <PaywallActions user={user} paypal={paypal} checkingOut={checkingOut} via={via} trialDays={trial_days} price={selected.price_usd} interval={selected.interval} onBuy={buy} onPass={buyPass} />
        </motion.div>
      </div>
    </section>
  );
}
